import {
  differenceInMinutes,
  format,
  fromUnixTime,
  isBefore,
  isValid,
  parse,
} from "date-fns";
import {
  fromIsoDateString,
  isoFormat,
  leadingZerosDateFormat,
  leadingZerosDayMonthFormat,
} from "../common/helpers/formatDateTime";
import { Nullable } from "../common/types/nullable";
import { Entry } from "../features/entries/entriesAPI";
import { Talent } from "../features/talents/talentAPI";

const DRAWER_WIDTH = 256;
const MAIN_PADDINGS = 32;
export const APP_BAR_HEIGHT = 64;

/** Set by HomeMenu from the measured AppBar height (toolbar + announcement banners). */
export const HOME_HEADER_HEIGHT_VAR = "--home-header-height";

export const HOME_LAYOUT_WIDTH = `calc(100vw - ${
  DRAWER_WIDTH + MAIN_PADDINGS
}px)`;
export const HOME_LAYOUT_HEIGHT = `calc(100vh - var(${HOME_HEADER_HEIGHT_VAR}, ${APP_BAR_HEIGHT}px) - ${MAIN_PADDINGS}px)`;

export const epochToString = (fmt: string, epochTs?: number): string => {
  if (epochTs && Number.isInteger(epochTs)) {
    return format(new Date(epochTs), fmt);
  }
  return "";
};

// ␞ = unicode record separator character

export const decodeLocalizedString = (localisedString: string) => {
  return localisedString.split("␞");
};

export const createLocalizedString = (stringValues: string[]) => {
  let localisedString = ``;
  stringValues.forEach((string, i) => {
    i !== 0
      ? (localisedString += `␞${string}`)
      : (localisedString += `${string}`);
  });
  return localisedString;
};

export const epochToDate = (epoch: EpochTimeStamp) => {
  const epochToUnix = Math.floor(epoch / 1000);
  return fromUnixTime(epochToUnix);
};

// Checks if two objects of same type have same content
export function areObjectsEqual<T extends Record<string, any>>(
  obj1: T | null,
  obj2: T | null,
): boolean {
  if (obj1 === null && obj2 === null) {
    return true;
  }

  if (obj1 === null || obj2 === null) {
    return false;
  }

  const keys1 = Object.keys(obj1) as (keyof T)[];
  const keys2 = Object.keys(obj2) as (keyof T)[];

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = obj1[key];
    const val2 = obj2[key];

    if (typeof val1 === "object" && typeof val2 === "object") {
      if (!areObjectsEqual(val1, val2)) {
        return false;
      }
    } else if (val1 !== val2) {
      return false;
    }
  }
  return true;
}

export function areArraysEqual<T extends Record<string, any>>(
  arr1: Array<T>,
  arr2: Array<T>,
): boolean {
  if (arr1.length !== arr2.length) {
    return false;
  }

  const sortedArr1 = [...arr1];
  const sortedArr2 = [...arr2];

  for (const obj1 of sortedArr1) {
    let foundEquivalent = false;

    for (const obj2 of sortedArr2) {
      if (areObjectsEqual(obj1, obj2)) {
        foundEquivalent = true;
        break; // Found equivalent, no need to check further
      }
    }

    if (!foundEquivalent) {
      return false; // Did not find an equivalent object in arr2 for an object in arr1
    }
  }

  return true;
}

export const getWorkTimeDuration = (
  endDate: Date,
  startDate: Date,
  lunchBreakDuration?: number | null,
) => {
  // First check difference in minutes
  let workDurationMinutes = differenceInMinutes(endDate, startDate);

  // If lunchBreakDuration is given, reduce it from workDurationMinutes
  if (lunchBreakDuration != null) {
    workDurationMinutes -= lunchBreakDuration;
  }

  const workDurationHours = Math.floor(workDurationMinutes / 60);
  const remainingMinutes = workDurationMinutes % 60;

  const duration = remainingMinutes
    ? `${workDurationHours}h ${remainingMinutes}min`
    : `${workDurationHours}h`;

  return duration;
};

export const calculateTotalDuration = (entries: Entry[]) => {
  let totalDurationMinutes = 0;

  entries.forEach((entry) => {
    const startDate = new Date(entry.start);
    const endDate = new Date(entry.end);
    const duration = getWorkTimeDuration(
      endDate,
      startDate,
      entry?.lunchBreak?.duration || null,
    );

    const durationParts = duration.split(" ");
    const hours = durationParts[0].replace("h", "");
    const minutes = durationParts[1]?.replace("min", "") || "0";
    const workDurationMinutes = parseInt(hours) * 60 + parseInt(minutes);

    totalDurationMinutes += workDurationMinutes;
  });

  const totalDurationHours = Math.floor(totalDurationMinutes / 60);
  const remainingMinutes = totalDurationMinutes % 60;

  const duration = remainingMinutes
    ? `${totalDurationHours}h ${remainingMinutes}min`
    : `${totalDurationHours}h`;

  return duration;
};

export const calculateMiddlePoint = (start: number, end: number) => {
  const middle = (start + end) / 2;
  return middle;
};

export const minutesToMs = (durationInMinutes: number) => {
  const durationInMs = durationInMinutes * 60000;
  return durationInMs;
};

export const calculateDurationInMinutes = (
  startTime: number,
  endTime: number,
) => {
  const durationMs = endTime - startTime;
  const durationMinutes = durationMs / 60000; // Convert duration to minutes
  return durationMinutes;
};

const isValidDay = (day: number): boolean => {
  return day >= 1 && day <= 31;
};

const isValidMonth = (month: number): boolean => {
  return month >= 1 && month <= 12;
};

const isValidYear = (year: number): boolean => {
  const currentYear = new Date().getFullYear();
  const realisticRange = { min: 1900, max: currentYear + 10 };
  return year >= realisticRange.min && year <= realisticRange.max;
};

const isValidComponents = (dateString: string, dateFormat: string): boolean => {
  const dayIndex = dateFormat.indexOf("dd");
  const monthIndex = dateFormat.indexOf("MM");
  const yearIndex = dateFormat.indexOf("yyyy");

  const day = parseInt(dateString.substring(dayIndex, dayIndex + 2));
  const month = parseInt(dateString.substring(monthIndex, monthIndex + 2));
  const year = parseInt(dateString.substring(yearIndex, yearIndex + 4));

  return isValidDay(day) && isValidMonth(month) && isValidYear(year);
};

export const validateDate = (
  date: string | Date,
  dateFormat: string,
): boolean => {
  // If date is empty, return false
  if (!date) {
    return false;
  }

  if (typeof date === "string") {
    const parsedDate = parse(date, dateFormat, new Date());
    if (!isValid(parsedDate) || !isValidComponents(date, dateFormat)) {
      return false;
    }
    date = parsedDate;
  }

  const day = date.getDate();
  const month = date.getMonth() + 1; // Months are zero-based, so add 1
  const year = date.getFullYear();

  return isValidDay(day) && isValidMonth(month) && isValidYear(year);
};

export const isDateBefore = (
  date: Nullable<Date | string>,
  date2: Date | string,
): boolean => {
  if (!date) return true;
  if (!validateDate(date, "")) return false;
  // TODO is there a better way to handle this than formatting to date string?
  const end = format(date, "yyyy-MM-dd");
  const before = isBefore(end, date2);
  return before;
};

// Converts date to YYYY-MM-DD string, ignoring timezone
export const convertDateToYYYYMMDDString = (
  date: Date,
  separator: string,
  reversed?: boolean,
) => {
  const dateObj = new Date(date);
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return !reversed
    ? `${year}${separator}${month}${separator}${day}`
    : `${day}${separator}${month}${separator}${year}`;
};

// Function to format start and end date strings (yyyy-MM-dd) into a specific format
// Can return single date string like dd.MM.yyyy or date range string like
// dd.MM - dd.MM.yyyy if years are same and dd.MM.yyyy - dd.MM.yyyy if years are different
export const formatDateStrings = (
  startDate: string,
  endDate?: string | null,
  hideYear?: boolean,
) => {
  // If endDate is missing, return the startDate in "dd.MM.yyyy" format
  if (!endDate) {
    return fromIsoDateString(startDate, leadingZerosDateFormat);
  }

  // Parse the start and end dates into Date objects
  const date1 = parse(startDate, isoFormat, new Date());
  const date2 = parse(endDate, isoFormat, new Date());

  // Get the years from the Date objects
  const year1 = date1.getFullYear();
  const year2 = date2.getFullYear();

  // If the years of start and end dates are the same, format the output with only the day and month of the start date
  if (year1 === year2) {
    return `${format(date1, leadingZerosDayMonthFormat)} - ${format(date2, hideYear ? leadingZerosDayMonthFormat : leadingZerosDateFormat)}`;
  } else {
    // If the years are different, format the output with the full start and end dates
    return `${format(date1, leadingZerosDateFormat)} - ${format(date2, hideYear ? leadingZerosDayMonthFormat : leadingZerosDateFormat)}`;
  }
};

/**
 * Formats a date range into a short string representation.
 *
 * @param startDate - The start date in "yyyy-MM-dd" format.
 * @param hiddenYear - An optional year to hide in the formatted output if it matches the year of the date(s).
 * @param endDate - An optional end date in "yyyy-MM-dd" format. If not provided, only the start date will be formatted.
 * @returns A formatted date string. If `endDate` is not provided, returns the `startDate` in "dd.MM.yyyy" format.
 *          If `hiddenYear` is provided and matches the year of the date(s), the year will be omitted from the output.
 *          If `endDate` is provided, returns a range in "dd.MM.yyyy - dd.MM.yyyy" format, with possible variations
 *          depending on the `hiddenYear` and whether the years of the start and end dates are the same.
 */
export const shortFormatDateStrings = (
  startDate: string,
  hiddenYear?: number,
  endDate?: string | null,
) => {
  // If endDate is missing, return the startDate in "dd.MM.yyyy" format
  if (!endDate || startDate === endDate) {
    const parsed = parse(startDate, isoFormat, new Date());
    if (hiddenYear && parsed.getFullYear() === hiddenYear) {
      return format(parsed, leadingZerosDayMonthFormat);
    } else {
      return format(parsed, leadingZerosDateFormat);
    }
  }

  // Parse the start and end dates into Date objects
  const date1 = parse(startDate, isoFormat, new Date());
  const date2 = parse(endDate, isoFormat, new Date());

  // Get the years from the Date objects
  const year1 = date1.getFullYear();
  const year2 = date2.getFullYear();

  if (hiddenYear) {
    if (year1 === year2) {
      // neither needs year as it is hidden
      return `${format(date1, leadingZerosDayMonthFormat)} - ${format(date2, leadingZerosDayMonthFormat)}`;
    } else {
      return `${format(date1, leadingZerosDateFormat)} - ${format(date2, leadingZerosDateFormat)}`;
    }
  } else {
    // If the years of start and end dates are the same, format the output with only the day and month of the start date
    if (year1 === year2) {
      return `${format(date1, leadingZerosDayMonthFormat)} - ${format(date2, leadingZerosDateFormat)}`;
    } else {
      // If the years are different, format the output with the full start and end dates
      return `${format(date1, leadingZerosDateFormat)} - ${format(date2, leadingZerosDateFormat)}`;
    }
  }
};

// Convert date to desired format or default: dd.MM.yyyy.
// null | undefined date converts to empty string
export const epochToFormatDate = (dt?: number, dateFormat?: string) => {
  if (!dt) return "";
  const date = new Date(dt);
  return format(date, dateFormat || "dd.MM.yyyy");
};

export const getTimeFromEpoch = (time?: number) => {
  if (!time) return "";
  const date = new Date(time);
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

export const formDateValidation = (
  date: Date | null,
  invalidDateMessage?: string | null,
) => {
  if (!date) return true;
  return validateDate(date, "date") || invalidDateMessage || "";
};

export const formDateRangeValidation = (
  startDate: Date | null,
  endDate: Date | null,
  invalidDateMessage?: string | null,
) => {
  if (!startDate || !endDate) return true;
  return startDate.getTime() <= endDate.getTime() || invalidDateMessage || "";
};

export const isPastDate = (date: Date) => {
  const now = new Date();
  return date.getTime() < now.getTime();
};

export const formDatePastDateValidation = (
  date: Date | null,
  invalidDateMessage?: string | null,
) => {
  if (!date) return true;
  return isPastDate(date) || invalidDateMessage || "";
};

/* 
- Normalizes input value to a string number with dot as decimal separator
- Also removes trailing zeros from the decimal part

Examples:
3,100.45 -> 3100.45
3.100,45 -> 3100.45
3.100,00 -> 3100
3,100.00 -> 3100
1,234,567.89 -> 1234567.89
1.234.567,89 -> 1234567.89
1.234.567.890 -> 1234567890
1,234,567,890 -> 1234567890
*/
export const normalizeInputValue = (input: string): string => {
  const dotCount = (input.match(/\./g) || []).length;
  const commaCount = (input.match(/,/g) || []).length;

  let normalizedInput = input;

  // Case: More than one dot or comma, but not both types present, treat them all as thousand separators
  if (
    (dotCount > 1 && commaCount === 0) ||
    (commaCount > 1 && dotCount === 0)
  ) {
    normalizedInput = input.replace(/[.,]/g, ""); // Remove all dots or commas
  } else if (dotCount > 0 && commaCount > 0) {
    // Mixed case: treat the last occurrence (comma or dot) as decimal separator, remove others.
    const lastCommaIndex = input.lastIndexOf(",");
    const lastDotIndex = input.lastIndexOf(".");
    if (lastCommaIndex > lastDotIndex) {
      // Last comma is treated as decimal separator
      normalizedInput =
        input.substring(0, lastCommaIndex).replace(/[.,]/g, "") +
        "." +
        input.substring(lastCommaIndex + 1).replace(",", ".");
    } else {
      // Last dot is treated as decimal separator
      normalizedInput =
        input.substring(0, lastDotIndex).replace(/[.,]/g, "") +
        "." +
        input.substring(lastDotIndex + 1).replace(".", ".");
    }
  } else if (commaCount === 1) {
    // Single comma present, replace it with a dot
    normalizedInput = input.replace(",", ".");
  } // No need to handle the single dot case separately as it's already in the correct format

  // Convert to a number to normalize the format and remove trailing zeros
  let finalNumber = parseFloat(normalizedInput).toString();

  // If the conversion results in an integer (no fractional part), remove unnecessary trailing zeros
  if (finalNumber.endsWith(".0")) {
    finalNumber = finalNumber.substring(0, finalNumber.length - 2);
  }

  return finalNumber;
};

export const getTalentFullName = (talent: Talent) => {
  if (!talent.account.firstName || !talent.account.lastName) return "";
  return `${talent.account.firstName} ${talent.account.lastName}`;
};

export const getIds = (array: { id: string }[]): string[] =>
  array.map((item) => item.id);

export const delay = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};
